/**
 * NFM (NewAudia Financial Management) Client Service
 * 
 * Handles all NFM-related API calls from the frontend.
 * 
 * SECURITY: Amount comes from server-side catalog, NEVER from client.
 * Client only sends product_type, product_id, and is_subscriber flag.
 * 
 * PRICING RULES:
 * - Subscriptions: Fixed price per tier/period
 * - Bundles: Dual pricing (subscriber vs a la carte)
 * - Engagement level evaluated at checkout time
 * 
 * PHASE III-B: Engagement Level Gate
 * - Must check engagement level before checkout
 * - Engagement level determines pricing authority
 */

// Get backend URL from environment
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Check if user needs to set engagement level before checkout
 * PHASE III-B: Engagement Gate
 * @param {string} userId - User ID
 * @param {string} [token] - Auth token
 * @returns {Promise<{requires_engagement_selection: boolean, current_level: string|null, message: string}>}
 */
export async function checkEngagementRequired(userId, token) {
  if (!userId) {
    return {
      requires_engagement_selection: true,
      current_level: null,
      message: 'User not authenticated'
    };
  }
  
  const headers = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  const response = await fetch(`${API_URL}/api/nfm/engagement/check-required?user_id=${userId}`, {
    headers
  });
  
  if (!response.ok) {
    throw new Error('Failed to check engagement status');
  }
  
  return response.json();
}

/**
 * Get user's current engagement level
 * @param {string} userId - User ID
 * @param {string} [token] - Auth token
 * @returns {Promise<{user_id: string, engagement_level: string|null, cadence: string|null, is_set: boolean, is_subscriber: boolean}>}
 */
export async function getEngagementStatus(userId, token) {
  const headers = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  const response = await fetch(`${API_URL}/api/nfm/engagement/status?user_id=${userId}`, {
    headers
  });
  
  if (!response.ok) {
    throw new Error('Failed to fetch engagement status');
  }
  
  return response.json();
}

/**
 * Set user's engagement level
 * @param {string} userId - User ID
 * @param {string} engagementLevel - Selected level: 'alacarte', 'novice', 'pro-reader', 'book-worm'
 * @param {string} [cadence] - Billing cadence for subscriptions: 'monthly' or 'annual'
 * @param {string} [token] - Auth token
 * @returns {Promise<{user_id: string, engagement_level: string, cadence: string|null, is_set: boolean, is_subscriber: boolean}>}
 */
export async function setEngagementLevel(userId, engagementLevel, cadence, token) {
  const headers = {
    'Content-Type': 'application/json'
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  const response = await fetch(`${API_URL}/api/nfm/engagement/set?user_id=${userId}`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      engagement_level: engagementLevel,
      cadence: cadence || null
    })
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to set engagement level' }));
    throw new Error(error.detail || 'Failed to set engagement level');
  }
  
  return response.json();
}

/**
 * Fetch available products from NFM catalog
 * Returns full pricing including subscription tiers and bundles with dual pricing
 * @returns {Promise<{subscription_tiers: Array, bundles: Array}>}
 */
export async function fetchNFMProducts() {
  const response = await fetch(`${API_URL}/api/nfm/reader/products`);
  
  if (!response.ok) {
    throw new Error('Failed to fetch products');
  }
  
  return response.json();
}

/**
 * Create a checkout session and redirect to Stripe
 * @param {Object} params
 * @param {string} params.productType - 'subscription' or 'bundle'
 * @param {string} params.productId - Product ID from catalog
 * @param {boolean} params.isSubscriber - Whether user is currently a subscriber (affects bundle pricing)
 * @param {string} [params.userId] - Authenticated user's ID
 * @param {string} [params.token] - Auth token (optional)
 * @returns {Promise<{checkout_url: string, session_id: string, transaction_id: string, amount_cents: number, credits: number}>}
 */
export async function createCheckoutSession({ productType, productId, isSubscriber = false, userId, token }) {
  const headers = {
    'Content-Type': 'application/json'
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  const response = await fetch(`${API_URL}/api/nfm/checkout/session`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      product_type: productType,
      product_id: productId,
      is_subscriber: isSubscriber,
      user_id: userId,
      origin_url: window.location.origin
    })
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Checkout failed' }));
    throw new Error(error.detail || 'Failed to create checkout session');
  }
  
  return response.json();
}

/**
 * Initiate checkout and redirect to Stripe
 * @param {Object} params
 * @param {string} params.productType - 'subscription' or 'bundle'
 * @param {string} params.productId - Product ID
 * @param {boolean} params.isSubscriber - Affects bundle pricing
 * @param {string} [params.userId] - Authenticated user's ID
 * @param {string} [params.token] - Auth token
 */
export async function initiateNFMCheckout({ productType, productId, isSubscriber = false, userId, token }) {
  const session = await createCheckoutSession({ productType, productId, isSubscriber, userId, token });
  
  // Redirect to Stripe Checkout
  window.location.href = session.checkout_url;
}

/**
 * Check checkout session status
 * @param {string} sessionId - Stripe session ID
 * @param {string} [token] - Auth token
 * @returns {Promise<{status: string, payment_status: string, product_type: string, product_id: string}>}
 */
export async function checkCheckoutStatus(sessionId, token) {
  const headers = {};
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  const response = await fetch(`${API_URL}/api/nfm/checkout/status/${sessionId}`, {
    headers
  });
  
  if (!response.ok) {
    throw new Error('Failed to check checkout status');
  }
  
  return response.json();
}

/**
 * Get reader's transaction history
 * @param {string} userId - User ID
 * @param {string} token - Auth token
 * @param {Object} [options]
 * @param {string} [options.month] - Filter by month (YYYY-MM)
 * @param {number} [options.limit=50] - Max transactions
 * @returns {Promise<{transactions: Array, total: number}>}
 */
export async function fetchTransactionHistory(userId, token, options = {}) {
  const params = new URLSearchParams({
    user_id: userId,
    limit: options.limit || 50
  });
  
  if (options.month) {
    params.append('month', options.month);
  }
  
  const response = await fetch(`${API_URL}/api/nfm/reader/transactions?${params}`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  
  if (!response.ok) {
    throw new Error('Failed to fetch transactions');
  }
  
  return response.json();
}

/**
 * Get reader's credit balance
 * @param {string} userId - User ID
 * @param {string} token - Auth token
 * @returns {Promise<{balance: number, total_earned: number, total_spent: number}>}
 */
export async function fetchCreditBalance(userId, token) {
  const response = await fetch(`${API_URL}/api/nfm/reader/balance?user_id=${userId}`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  
  if (!response.ok) {
    throw new Error('Failed to fetch balance');
  }
  
  return response.json();
}

/**
 * Get subscription status
 * @param {string} userId - User ID
 * @param {string} token - Auth token
 * @returns {Promise<{has_subscription: boolean, plan: string, status: string, ...}>}
 */
export async function fetchSubscriptionStatus(userId, token) {
  const response = await fetch(`${API_URL}/api/nfm/subscription/status?user_id=${userId}`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  
  if (!response.ok) {
    throw new Error('Failed to fetch subscription status');
  }
  
  return response.json();
}

/**
 * Cancel subscription
 * @param {string} userId - User ID
 * @param {string} token - Auth token
 * @returns {Promise<{status: string, access_until: string, remaining_credits: number}>}
 */
export async function cancelSubscription(userId, token) {
  const response = await fetch(`${API_URL}/api/nfm/subscription/cancel?user_id=${userId}`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Cancel failed' }));
    throw new Error(error.detail || 'Failed to cancel subscription');
  }
  
  return response.json();
}

// NFM Product Type Constants
export const NFM_PRODUCT_TYPES = {
  SUBSCRIPTION: 'subscription',
  BUNDLE: 'bundle'
};

// NFM Subscription Plan IDs (tier_period format)
export const NFM_PLANS = {
  NOVICE_MONTHLY: 'novice_monthly',
  NOVICE_ANNUAL: 'novice_annual',
  PRO_READER_MONTHLY: 'pro_reader_monthly',
  PRO_READER_ANNUAL: 'pro_reader_annual',
  BOOK_WORM_MONTHLY: 'book_worm_monthly',
  BOOK_WORM_ANNUAL: 'book_worm_annual'
};

// NFM Bundle IDs
export const NFM_BUNDLES = {
  BOOSTER_PACK: 'booster_pack',
  POWER_PACK: 'power_pack',
  VALUE_PACK: 'value_pack',
  PRO_PACK: 'pro_pack'
};

/**
 * Helper: Format price from cents to display string
 * @param {number} cents - Price in cents
 * @returns {string} Formatted price (e.g., "$10.00")
 */
export function formatPrice(cents) {
  return `$${(cents / 100).toFixed(2)}`;
}

/**
 * Helper: Get bundle price based on subscriber status
 * @param {Object} bundle - Bundle object with dual pricing
 * @param {boolean} isSubscriber - Whether user is subscriber
 * @returns {number} Price in cents
 */
export function getBundlePrice(bundle, isSubscriber) {
  return isSubscriber ? bundle.subscriber_price_cents : bundle.alacarte_price_cents;
}
